'use client';

import { useRef } from 'react';

const Card1 = () => (
  <div className="flex h-full">
    <div className="rounded-lg bg-white p-4 md:p-6 lg:p-8 flex grow">
      <div className="space-y-4">
        <h3 className="text-policeblue-dark font-bold">Qui sommes-nous</h3>

        <p className="text-charcoal font-semibold text-justify">
          Nous sommes un acteur engagé de la transformation du marché de l&apos;emploi.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          À la croisée des parcours des talents de 45&nbsp;ans et plus et des besoins réels des
          entreprises, nous réinventons le lien entre expérience, agilité et performance.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          Notre conviction :<br />
          L&apos;avenir se construit avec celles et ceux qui ont su traverser les mutations, et qui
          savent aujourd&apos;hui composer avec l&apos;intelligence humaine comme avec
          l&apos;intelligence artificielle.
        </p>
      </div>
    </div>
  </div>
);

const Card2 = () => (
  <div className="flex h-full">
    <div className="rounded-lg bg-white p-4 md:p-6 lg:p-8 flex grow">
      <div className="space-y-4">
        <h3 className="text-policeblue-dark font-bold">Notre mission</h3>

        <p className="text-charcoal font-semibold text-justify">
          Révéler la puissance des profils de 45&nbsp;ans et plus dans les environnements de travail
          d&apos;aujourd&apos;hui.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          En facilitant la rencontre entre les entreprises et des talents expérimentés, nous créons
          des alliances durables, qui allient sens, valeur ajoutée et impact immédiat.
        </p>

        <div className="text-charcoal font-semibold text-justify">
          <p>Nous croyons à une nouvelle manière de recruter :</p>

          <ul className="list-inside list-disc">
            <li>plus directe</li>
            <li>plus humaine</li>
            <li>plus stratégique</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
);

const Card3 = () => (
  <div className="flex h-full">
    <div className="rounded-lg bg-white p-4 md:p-6 lg:p-8 flex grow">
      <div className="space-y-4">
        <h3 className="text-policeblue-dark font-bold">Ce que nous apportons aux entreprises</h3>

        <p className="text-charcoal font-semibold text-justify">
          Un regard neuf sur l&apos;expérience.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          Nous allons à la rencontre des décideurs pour identifier les besoins au-delà des mots.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          Nous traduisons les enjeux opérationnels en profils concrets, capables d&apos;entrer en
          action rapidement, avec fiabilité et recul.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          Grâce à notre vivier de talents de 45&nbsp;ans et plus, nous proposons des solutions sur
          mesure, pensées pour faire avancer vos projets.
        </p>
      </div>
    </div>
  </div>
);

const Card4 = () => (
  <div className="flex h-full">
    <div className="rounded-lg bg-white p-4 md:p-6 lg:p-8 flex grow">
      <div className="space-y-4">
        <h3 className="text-policeblue-dark font-bold">Ce que nous apportons aux candidats</h3>

        <p className="text-charcoal font-semibold text-justify">
          Un accompagnement respectueux, exigeant et tourné vers l&apos;avenir.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          Nous valorisons les parcours, même atypiques.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          Nous aidons nos candidats de 45&nbsp;ans et plus à repositionner leurs compétences dans un
          monde du travail en pleine mutation.
        </p>

        <p className="text-charcoal font-semibold text-justify">
          Parce que nous connaissons les entreprises de l&apos;intérieur, nous pouvons les mettre en
          relation avec des opportunités qui font sens et dans lesquelles ils peuvent pleinement
          s&apos;épanouir.
        </p>
      </div>
    </div>
  </div>
);

const WeAre = () => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const scrollLeft = () => {
    const el = scrollRef.current;
    if (!el) return;

    const isAtStart = el.scrollLeft <= 10;

    if (isAtStart) {
      el.scrollTo({ left: el.scrollWidth, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: -300, behavior: 'smooth' });
    }
  };

  const scrollRight = () => {
    const el = scrollRef.current;
    if (!el) return;

    const isAtEnd = el.scrollLeft + el.clientWidth >= el.scrollWidth - 10;

    if (isAtEnd) {
      el.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      el.scrollBy({ left: 300, behavior: 'smooth' });
    }
  };

  return (
    <section className="bg-policeblue-dark p-4 sm:p-8 md:p-10 lg:p-12 flex justify-center">
      <div className="gap-4 max-w-6xl relative m-auto flex w-full flex-col">
        <div className="flex flex-row justify-between">
          <h2 className="text-stonerwhite flex justify-center">Qui est Adequa</h2>

          <div className="sm:flex sm:flex-row sm:gap-4 hidden">
            <button
              onClick={scrollLeft}
              className="bg-stonerwhite h-10 w-10 shadow-lg flex cursor-pointer items-center justify-center rounded-full transition-all hover:scale-110 active:scale-110"
            >
              <i className="fi fi-rr-arrow-left text-policeblue-dark -mb-1 text-lg leading-none"></i>
            </button>

            <button
              onClick={scrollRight}
              className="bg-stonerwhite h-10 w-10 shadow-lg flex cursor-pointer items-center justify-center rounded-full transition-all hover:scale-110 active:scale-110"
            >
              <i className="fi fi-rr-arrow-right text-policeblue-dark -mb-1 text-lg leading-none"></i>
            </button>
          </div>
        </div>

        <div ref={scrollRef} className="carousel carousel-center rounded-box space-x-4 w-full">
          <div id="slide1" className="carousel-item w-78 md:w-125">
            <Card1 />
          </div>

          <div id="slide2" className="carousel-item w-78 md:w-125">
            <Card2 />
          </div>

          <div id="slide3" className="carousel-item w-78 md:w-125">
            <Card3 />
          </div>

          <div id="slide4" className="carousel-item w-78 md:w-125">
            <Card4 />
          </div>
        </div>
      </div>
    </section>
  );
};

export default WeAre;
